/* mm-global: the shared Maison runtime, loaded deferred in layout/theme.liquid.
   - window.MaisonEngine: bundle maths ported unchanged from the live theme's assets/mm-bundle.js (2026-10-21), so every
     total the theme shows matches what the Maison engine discount charges. The engine stays the source of truth.
   - window.MM: cart helpers (add / change / refresh, one event for everything) and small shared UI elements.
   Every behaviour is a custom element, so it initialises itself when a section is re-rendered in the theme editor. */
(() => {
  const root = window.Shopify?.routes?.root || '/';
  const rate = () => Number(window.Shopify?.currency?.rate || 1);
  const currency = () => window.Shopify?.currency?.active || 'USD';

  /* ---------- Engine config + maths (verbatim from live mm-bundle.js) ---------- */
  let cfg;
  const config = () => {
    if (cfg !== undefined) return cfg;
    try { cfg = JSON.parse(document.getElementById('mm-engine-config')?.textContent || 'null'); } catch { cfg = null; }
    return cfg;
  };
  const money = (cents) => {
    // Every currency drops zero cents ($59, €30), like the theme's Liquid prices (snippets/mm-money).
    const whole = cents % 100 === 0;
    try { return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: currency(), currencyDisplay: 'narrowSymbol', ...(whole ? { minimumFractionDigits: 0, maximumFractionDigits: 0 } : {}) }).format(cents / 100); }
    catch { return `$${(cents / 100).toFixed(2)}`; }
  };
  // Same rule as engine.ts tierTotal(): fixed totals per listed quantity, top tier's unit price above it.
  const tierTotal = (totals, qty) => {
    const qs = Object.keys(totals).map(Number).filter((q) => q > 1).sort((a, b) => a - b);
    if (!qs.length || qty < qs[0]) return null;
    if (totals[qty] != null) return totals[qty];
    const top = qs.filter((q) => q <= qty).pop();
    return (totals[top] / top) * qty;
  };
  const toCents = (shopAmount) => Math.round(shopAmount * rate() * 100);
  // Bundle tier totals: same rule as the engine (engine.ts tierAmount): USD as set, other currencies rounded UP to a
  // whole unit like the store's market prices.
  const tierCents = (totalUsd) => (currency() === 'USD' ? Math.round(totalUsd * 100) : Math.ceil(Math.round(totalUsd * rate() * 100) / 100 - 1e-9) * 100);
  // Same as engine.ts tierPrice: listed tiers whole outside USD; above the top tier, the top tier's per-bag price.
  const tierCentsQty = (totals, qty) => {
    const usd = tierTotal(totals, qty);
    if (usd == null) return null;
    if (currency() === 'USD' || totals[qty] != null) return tierCents(currency() === 'USD' ? usd : totals[qty]);
    const top = Object.keys(totals).map(Number).filter((q) => q > 1 && q <= qty).sort((a, b) => a - b).pop();
    return Math.round((tierCents(totals[top]) / top) * qty);
  };
  // Version A, same as engine.ts tierPriceLocal: outside USD a tier keeps its USD % off against this market's own
  // prices (hand-set EUR included). singlesCents = what these bags cost singly here. Without tier.unit: old rule.
  // Fixed per-currency totals, same as engine.ts tierPriceSet (2026-10-24): tier.prices[currency][qty] is charged
  // exactly; above the top tier, that currency's top total ÷ top per bag. null = no entry, the rules below apply.
  const tierSetCents = (tier, qty) => {
    const local = tier.prices?.[currency()];
    if (!local) return null;
    const qs = Object.keys(tier.totals).map(Number).filter((q) => q > 1).sort((a, b) => a - b);
    if (!qs.length || qty < qs[0]) return null;
    const at = (q) => { const v = local[q]; return typeof v === 'number' && Number.isFinite(v) && v > 0 ? Math.round(v * 100) : null; };
    if (tier.totals[qty] != null) return at(qty);
    const top = qs.filter((q) => q <= qty).pop();
    const topCents = at(top);
    return topCents == null ? null : Math.round((topCents / top) * qty);
  };
  const tierLocalCents = (tier, qty, singlesCents) => {
    const set = tierSetCents(tier, qty);
    if (set != null) return Math.min(set, singlesCents);
    if (currency() === 'USD' || !tier.unit) return tierCentsQty(tier.totals, qty);
    const qs = Object.keys(tier.totals).map(Number).filter((q) => q > 1).sort((a, b) => a - b);
    if (!qs.length || qty < qs[0]) return null;
    const ratio = (q) => tier.totals[q] / (q * tier.unit);
    let total;
    if (tier.totals[qty] != null) total = Math.ceil(Math.round(singlesCents * ratio(qty)) / 100 - 1e-9) * 100;
    else {
      const top = qs.filter((q) => q <= qty).pop();
      const perBag = (Math.ceil(Math.round((singlesCents / qty) * top * ratio(top)) / 100 - 1e-9) * 100) / top;
      total = Math.round(perBag * qty);
    }
    return Math.min(total, singlesCents);
  };

  /* ---------- Colours ---------- */
  // Colour name → CSS background. Sources (first wins): swatches rendered on this page (Shopify native swatches,
  // data-mm-swatch-name + --swatch), then Theme settings › Color swatch ("Pearl White:#F8F6F0" lines, live's map).
  // "(Limited Edition)" / "(Pre-order | Ships …)" suffixes are ignored when matching.
  const clean = (name) => String(name || '').replace(/\s*\(.*?\)\s*/g, '').trim();
  let swatchMap;
  const swatchOf = (name) => {
    if (!swatchMap) {
      swatchMap = {};
      try {
        for (const line of JSON.parse(document.getElementById('mm-swatch-config')?.textContent || '""').split(/\n/)) {
          const i = line.lastIndexOf(':');
          if (i > 0) swatchMap[clean(line.slice(0, i)).toLowerCase()] = line.slice(i + 1).trim();
        }
      } catch {}
    }
    const v = swatchMap[clean(name).toLowerCase()];
    return v && /^#|^rgb/i.test(v) ? v : null;
  };
  const nativeOf = (name) => {
    const key = clean(name).toLowerCase();
    for (const el of document.querySelectorAll('[data-mm-swatch-name]')) {
      if (clean(el.dataset.mmSwatchName).toLowerCase() === key) {
        const bg = el.style.getPropertyValue('--swatch').trim();
        if (bg) return bg;
      }
    }
    return null;
  };
  const swatchBg = (name) => nativeOf(name) || swatchOf(name);
  const dot = (variant, size = '') => {
    const name = variant.option1 || variant.title;
    const bg = swatchBg(name);
    const img = variant.featured_image?.src;
    const style = bg ? `--swatch:${bg}` : img ? `--swatch:url('${img}${img.includes('?') ? '&' : '?'}width=96')` : '';
    return `<span class="mm-swatch${size ? ` mm-swatch--${size}` : ''}" style="${style}" aria-hidden="true"></span>`;
  };
  // "Midnight Black (Pre-order | Ships End of Oct)" → "Ships End of Oct" (the variant title carries the pre-order note).
  const preNote = (variant) => { const m = (variant?.title || '').match(/\(\s*pre-?order\s*\|?\s*([^)]*)\)/i); return m ? (m[1].trim() || 'Ships when ready') : null; };
  const isPre = (variant) => /pre-?order/i.test(variant?.title || '');

  /* ---------- Cart ---------- */
  // One event for every cart change the theme makes: 'mm:cart:updated' { cart, open }. The cart drawer, header count
  // and cart page listen to it. Horizon's own add-to-cart ('shopify:cart:lines-update') is bridged into it below.
  const cartType = () => document.documentElement.dataset.mmCartType || 'drawer';
  const fetchCart = async () => (await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } })).json();
  const announce = (cart, open = false) => document.dispatchEvent(new CustomEvent('mm:cart:updated', { detail: { cart, open } }));
  const post = async (path, body) => {
    const res = await fetch(`${root}${path}`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: JSON.stringify(body) });
    const json = await res.json().catch(() => ({})); // a 429/5xx comes back as an HTML page
    if (!res.ok) throw new Error(json.description || json.message || 'Could not update your cart. Please try again in a moment.');
    return json;
  };
  // Add in one request (live's payload shape: { items: [{ id, quantity, properties? }] }), then open the drawer.
  const addItems = async (items, { open = true } = {}) => {
    try { await post('cart/add.js', { items }); }
    catch (e) { throw new Error(e.message || 'Could not add to your cart. Please try again in a moment.'); }
    if (cartType() === 'page') { window.location.href = `${root}cart`; return null; }
    const cart = await fetchCart();
    announce(cart, open);
    return cart;
  };
  const changeLine = async (key, quantity) => { await post('cart/change.js', { id: key, quantity }); const cart = await fetchCart(); announce(cart); return cart; };
  const updateLines = async (updates) => { await post('cart/update.js', { updates }); const cart = await fetchCart(); announce(cart); return cart; };
  const refresh = async (open = false) => { const cart = await fetchCart(); announce(cart, open); return cart; };
  document.addEventListener('shopify:cart:lines-update', () => setTimeout(() => refresh(true), 50));
  // The bundle package buttons (snippets/mm-bundle-pack-assets) ask for a refresh this way, as on live.
  document.addEventListener('cart:refresh', () => refresh());

  window.MaisonEngine = { config, money, tierTotal, toCents, tierCents, tierCentsQty, tierSetCents, tierLocalCents, addItems, dot, preNote, isPre, clean, swatchBg };
  window.MM = { root, money, currency, fetchCart, addItems, changeLine, updateLines, refresh, announce, clean, swatchBg, dot, isPre, preNote };

  /* ---------- Shared elements ---------- */
  const define = (name, cls) => { if (!customElements.get(name)) customElements.define(name, cls); };

  // <mm-carousel>: a .mm-scroller track ([data-track]) with optional [data-prev]/[data-next] buttons anywhere inside.
  // Mouse/trackpad shoppers get click-and-drag too (touch already scrolls natively; this only binds pointer types
  // that don't, 2026-10-26: arrows alone weren't a discoverable "scroll" affordance on desktop).
  define('mm-carousel', class extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      if (!this.track) return;
      this.prev = this.querySelector('[data-prev]');
      this.next = this.querySelector('[data-next]');
      const step = (dir) => {
        const item = this.track.firstElementChild;
        const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
        const w = item ? item.getBoundingClientRect().width + gap : this.track.clientWidth * .8;
        const per = Math.max(1, Math.floor((this.track.clientWidth + gap) / w));
        this.track.scrollBy({ left: dir * w * per, behavior: 'smooth' });
      };
      this.prev?.addEventListener('click', () => step(-1));
      this.next?.addEventListener('click', () => step(1));
      this.sync = () => {
        const max = this.track.scrollWidth - this.track.clientWidth - 2;
        if (this.prev) this.prev.disabled = this.track.scrollLeft <= 2;
        if (this.next) this.next.disabled = this.track.scrollLeft >= max;
        this.toggleAttribute('data-static', max <= 2);
      };
      this.track.addEventListener('scroll', this.sync, { passive: true });
      this.ro = new ResizeObserver(this.sync); this.ro.observe(this.track);
      this.sync();

      let dragging = false, moved = false, startX = 0, startScroll = 0;
      this.track.addEventListener('pointerdown', (e) => {
        if (e.pointerType === 'touch') return; // native touch scroll already works
        dragging = true; moved = false; startX = e.clientX; startScroll = this.track.scrollLeft;
        this.track.setPointerCapture(e.pointerId);
        this.track.classList.add('is-dragging');
      });
      this.track.addEventListener('pointermove', (e) => {
        if (!dragging) return;
        const dx = e.clientX - startX;
        if (Math.abs(dx) > 4) moved = true;
        this.track.scrollLeft = startScroll - dx;
      });
      const endDrag = () => { dragging = false; this.track.classList.remove('is-dragging'); };
      this.track.addEventListener('pointerup', endDrag);
      this.track.addEventListener('pointercancel', endDrag);
      this.track.addEventListener('pointerleave', endDrag);
      // A drag shouldn't also fire the link/button the pointer happened to land on.
      this.track.addEventListener('click', (e) => { if (moved) { e.preventDefault(); e.stopPropagation(); moved = false; } }, { capture: true });
    }
    disconnectedCallback() { this.ro?.disconnect(); }
  });

  // <mm-tabs>: [data-tab="x"] buttons switch [data-panel="x"]; first tab (or [data-default]) starts open.
  define('mm-tabs', class extends HTMLElement {
    connectedCallback() {
      this.tabs = [...this.querySelectorAll('[data-tab]')];
      this.panels = [...this.querySelectorAll('[data-panel]')];
      this.tabs.forEach((t) => t.addEventListener('click', () => this.show(t.dataset.tab)));
      this.show((this.tabs.find((t) => t.hasAttribute('data-default')) || this.tabs[0])?.dataset.tab);
    }
    show(id) {
      this.tabs.forEach((t) => { const on = t.dataset.tab === id; t.setAttribute('aria-selected', on); t.classList.toggle('is-on', on); });
      this.panels.forEach((p) => { p.hidden = p.dataset.panel !== id; });
    }
  });

  // <mm-reveal-bar data-after="#selector" [data-when="away"]>: adds .is-visible once the target has scrolled above the
  // viewport (sticky add-to-cart bars); with data-when="away", whenever the target is off screen in either direction, so
  // a phone shopper still reading above a far-down button has one. Hides again near the footer so it never covers it.
  define('mm-reveal-bar', class extends HTMLElement {
    connectedCallback() {
      const target = document.querySelector(this.dataset.after);
      if (!target) return;
      const away = this.dataset.when === 'away';
      let past = false, atFooter = false;
      const set = () => this.classList.toggle('is-visible', past && !atFooter);
      this.io = new IntersectionObserver(([e]) => { past = !e.isIntersecting && (away || e.boundingClientRect.top < 0); set(); });
      this.io.observe(target);
      // Jump-scrolls (anchor links, restored scroll) can skip the observer edge: re-check the position on scroll and load.
      const check = () => { const r = target.getBoundingClientRect(); past = away ? (r.bottom < 0 || r.top > innerHeight) : r.bottom < 0; set(); };
      this.onScroll = () => { if (!this.raf) this.raf = requestAnimationFrame(() => { this.raf = 0; check(); }); };
      window.addEventListener("scroll", this.onScroll, { passive: true, capture: true });
      window.addEventListener("load", check); requestAnimationFrame(check);
      const footer = document.querySelector('footer');
      if (footer) { this.io2 = new IntersectionObserver(([e]) => { atFooter = e.isIntersecting; set(); }); this.io2.observe(footer); }
    }
    disconnectedCallback() { this.io?.disconnect(); this.io2?.disconnect(); window.removeEventListener("scroll", this.onScroll, { capture: true }); }
  });

  // <mm-cart-count>: keeps the header bubble in step with the cart.
  define('mm-cart-count', class extends HTMLElement {
    connectedCallback() {
      this.on = (e) => { const n = e.detail?.cart?.item_count ?? 0; this.textContent = n; this.hidden = n === 0; };
      document.addEventListener('mm:cart:updated', this.on);
    }
    disconnectedCallback() { document.removeEventListener('mm:cart:updated', this.on); }
  });
  // <mm-product-card>: colour taps swap the photo and the variant; [data-quick-add] adds the selected colour.
  define('mm-product-card', class extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (e) => {
        const sw = e.target.closest('[data-variant]');
        if (sw && this.contains(sw)) {
          e.preventDefault();
          if (sw.disabled) return;
          this.dataset.variantId = sw.dataset.variant;
          this.querySelectorAll('[data-variant]').forEach((b) => { const on = b === sw; b.classList.toggle('is-on', on); b.setAttribute('aria-checked', on); });
          const img = this.querySelector('.mm-pcard__img-main');
          if (img && sw.dataset.img) { img.removeAttribute('srcset'); img.src = sw.dataset.img; }
          this.querySelectorAll('a[href]').forEach((a) => { const u = new URL(a.href, location.href); if (u.pathname === new URL(this.dataset.productUrl, location.href).pathname) { u.searchParams.set('variant', sw.dataset.variant); a.href = u.pathname + u.search; } });
          return;
        }
        const add = e.target.closest('[data-quick-add]');
        if (add && this.contains(add)) { e.preventDefault(); this.add(add); }
      });
    }
    async add(btn) {
      if (btn.getAttribute('aria-busy') === 'true') return;
      const err = this.querySelector('[data-error]');
      btn.setAttribute('aria-busy', 'true'); if (err) err.hidden = true;
      try { await addItems([{ id: Number(this.dataset.variantId), quantity: 1 }]); }
      catch (e) { if (err) { err.textContent = e.message; err.hidden = false; } }
      finally { btn.removeAttribute('aria-busy'); }
    }
  });
  /* Ships-by estimate: exact port of our in-house estimator (maison-redesign blocks/estimated-delivery.liquid, which
     replaced the Essentials Estimated Delivery app), shared by the PDP, Trio hero, Home hero, collection hero and cart.
     Inputs come from snippets/mm-ship-by-attrs.liquid (processing days + product metafield override, cutoff, store UTC
     offset, per-country ranges). Country: localStorage edd_country (1-hour cache, edd_country_time), else ipapi.co
     (3 s timeout, then cached); rule = that country, else EU for the 27 EU codes, else INTL; no country = the surface's
     own min/max and no label. Store time = now at the fixed offset; before the cutoff on a business day the order ships
     after the processing days, otherwise from the next business day. Delivery = ship date + min…max business days,
     weekends skipped. Dates en-US "Mon, Oct 27"; range "A - B" (one date when min = max); label "(United Kingdom)" in
     [data-country-display]. Pre-order (PDP, data-pre-hide): hidden while the selected variant's title has "(Pre-order",
     as the estimator was hidden by the pre-order alert. */
  const fill = (tpl, vars) => String(tpl || '').replace(/\{(\w+)\}/g, (m, k) => (vars[k] ?? m));
  const EDD_EU = ['AT','BE','BG','HR','CY','CZ','DK','EE','FI','FR','DE','GR','HU','IE','IT','LV','LT','LU','MT','NL','PL','PT','RO','SK','SI','ES','SE'];
  let eddLookup = null; // one ipapi.co request per page, shared by every <mm-ship-by>
  const eddCountry = () => {
    try {
      const cached = localStorage.getItem('edd_country');
      const cachedTime = localStorage.getItem('edd_country_time');
      if (cached && cachedTime && (Date.now() - parseInt(cachedTime, 10)) < 3600000) return Promise.resolve(cached);
    } catch (e) { /* storage blocked: look it up */ }
    return (eddLookup ||= (async () => {
      try {
        const response = await fetch('https://ipapi.co/json/', { signal: AbortSignal.timeout(3000) });
        if (response.ok) {
          const data = await response.json();
          const countryCode = data.country_code || data.country;
          if (countryCode) {
            localStorage.setItem('edd_country', countryCode);
            localStorage.setItem('edd_country_time', Date.now().toString());
            return countryCode;
          }
        }
      } catch (e) { /* use defaults */ }
      return null;
    })());
  };
  const eddPre = (variant) => /\((pre-order|preorder)/i.test(variant?.title || '');
  define('mm-ship-by', class extends HTMLElement {
    connectedCallback() {
      if (this.done) return;
      this.done = true;
      const d = this.dataset;
      let rules = {};
      try { rules = JSON.parse(d.rules || '{}'); } catch (e) { rules = {}; }
      this.cfg = {
        processingDays: parseInt(d.processing, 10) || 3,
        shippingMin: parseInt(d.min, 10) || 5,
        shippingMax: parseInt(d.max, 10) || 12,
        cutoffHour: parseInt(d.cutoff, 10) || 14,
        timezoneOffset: parseInt(d.tzOffset, 10) || -5,
        skipWeekends: d.skipWeekends !== 'false',
        enableGeo: d.geo !== 'false',
        rules,
      };
      if (d.preHide === 'true') {
        this.scope = this.closest('mm-pdp') || document;
        this.onVariant = (e) => { if (e.detail?.variant) this.style.display = eddPre(e.detail.variant) ? 'none' : ''; };
        this.scope.addEventListener('mm:variant:change', this.onVariant);
        if (d.pre === 'true') this.style.display = 'none';
      }
      this.init();
    }
    disconnectedCallback() {
      clearInterval(this.timer);
      if (this.onVariant) this.scope.removeEventListener('mm:variant:change', this.onVariant);
      this.done = false;
    }
    async init() {
      if (this.cfg.enableGeo) {
        const cc = await eddCountry();
        if (cc) this.applyCountry(cc);
      }
      this.calc();
      if (this.dataset.countdown === 'true' && this.querySelector('[data-countdown]')) this.timer = setInterval(() => this.calc(), 1000);
    }
    applyCountry(code) {
      const r = this.cfg.rules;
      let rule = r[code];
      if (!rule && EDD_EU.includes(code)) rule = r.EU;
      if (!rule) rule = r.INTL;
      if (!rule) return;
      this.cfg.shippingMin = rule.min;
      this.cfg.shippingMax = rule.max;
      this.country = code;
      this.querySelectorAll('[data-country-display]').forEach((el) => { el.textContent = `(${rule.name || code})`; });
    }
    storeTime() {
      const now = new Date();
      const utc = now.getTime() + (now.getTimezoneOffset() * 60000);
      return new Date(utc + (this.cfg.timezoneOffset * 3600000));
    }
    isBiz(date) { const day = date.getDay(); return !(this.cfg.skipWeekends && (day === 0 || day === 6)); }
    addBiz(start, days) {
      const date = new Date(start);
      let added = 0;
      while (added < days) { date.setDate(date.getDate() + 1); if (this.isBiz(date)) added++; }
      return date;
    }
    fmt(date) { return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }); }
    calc() {
      const c = this.cfg;
      const store = this.storeTime();
      const before = (store.getHours() * 60 + store.getMinutes()) < (c.cutoffHour * 60);
      const today = before && this.isBiz(store);
      const ship = today ? this.addBiz(store, c.processingDays) : this.addBiz(this.addBiz(store, 1), c.processingDays);
      const dMin = this.addBiz(ship, c.shippingMin);
      const dMax = this.addBiz(ship, c.shippingMax);
      this.dates = { ship, min: dMin, max: dMax };
      const shipEl = this.querySelector('[data-ship-date]');
      const delEl = this.querySelector('[data-delivery-date]');
      if (shipEl) shipEl.textContent = this.fmt(ship);
      if (delEl) delEl.textContent = c.shippingMin === c.shippingMax ? this.fmt(dMin) : `${this.fmt(dMin)} - ${this.fmt(dMax)}`;
      const cd = this.querySelector('[data-countdown]');
      if (cd) {
        const cutoff = new Date(store); cutoff.setHours(c.cutoffHour, 0, 0, 0);
        const diff = cutoff.getTime() - store.getTime();
        cd.hidden = !(this.dataset.countdown === 'true' && today && diff > 0);
        if (cd.hidden) { clearInterval(this.timer); return; }
        const h = Math.floor(diff / 3600000);
        const m = Math.floor((diff % 3600000) / 60000);
        const s = Math.floor((diff % 60000) / 1000);
        cd.textContent = fill(this.dataset.tCountdown, { time: `${h}h ${String(m).padStart(2, '0')}m ${String(s).padStart(2, '0')}s`, date: this.fmt(ship) });
      }
    }
  });

  // Support chat bubble (Commslayer / Chatwoot, fixed bottom-right) steps aside while a buy area is on screen, where it
  // would sit on the price or the button (R2 2026-10-22). It comes back as soon as the buy area scrolls away.
  (() => {
    const SEL = '[data-mm-chat-clear], .mm-trio-hero__buy, .mm-pdp__buy, .mm-pdp__price, mm-bundle-picker, mm-trio-builder';
    const seen = new Set();
    let io;
    const sync = () => document.documentElement.classList.toggle('mm-chat-clear', seen.size > 0);
    const watch = () => {
      if (!('IntersectionObserver' in window)) return;
      io ||= new IntersectionObserver((entries) => { for (const e of entries) e.isIntersecting ? seen.add(e.target) : seen.delete(e.target); sync(); }, { rootMargin: '0px 0px -64px 0px' });
      document.querySelectorAll(SEL).forEach((el) => io.observe(el));
    };
    document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', watch) : watch();
    document.addEventListener('shopify:section:load', () => { seen.clear(); io?.disconnect(); io = null; watch(); });
  })();
})();
